import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { RealtimeEvent } from '@/types/email';
import type { EventStreamStatus } from '@/realtime/useEventStream';
import { getEventDedupKey } from '@/lib/eventDeduplication';
import { handleRealtimeEvent, type RealtimeEventContext } from '@/realtime/handleRealtimeEvent';
import { mailEvents } from '@/events/mailEvents';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';

const RELOAD_DEBOUNCE_MS = 250;
const RELOAD_MAX_DELAY_MS = 1500;
const MAX_QUEUE_SIZE = 100; // Prevent unbounded queue growth while tab is hidden
const HIDDEN_THRESHOLD_MS = 5 * 60 * 1000; // Only force reload after 5+ minutes hidden

type PendingReload = { accountId: string; folderId: string } | null;

interface RealtimeStoreState {
  queue: RealtimeEvent[];
  processing: boolean;
  documentVisible: boolean;
  hiddenAt: number | null;
  /** Events were silently dropped from the hidden-tab queue (cap overflow). */
  queueOverflowed: boolean;
  reloadRequest: PendingReload;
  reloadTimer: number | null;
  reloadDeadline: number | null;
  realtimeStatus: EventStreamStatus;
  showRealtimeConnecting: boolean;
  enqueueEvent: (event: RealtimeEvent) => void;
  processQueue: () => Promise<void>;
  setDocumentVisibility: (visible: boolean) => void;
  scheduleMailboxReload: (accountId: string, folderId: string) => void;
  cancelScheduledReload: () => void;
  flushScheduledReload: () => void;
  setRealtimeStatus: (status: EventStreamStatus) => void;
  setShowRealtimeConnecting: (visible: boolean) => void;
  clearQueue: () => void;
}

export const useRealtimeStore = create<RealtimeStoreState>()(
  conditionalDevtools(
    (set, get) => ({
      queue: [],
      processing: false,
      documentVisible: true,
      hiddenAt: null,
      queueOverflowed: false,
      reloadRequest: null,
      reloadTimer: null,
      reloadDeadline: null,
      realtimeStatus: 'idle',
      showRealtimeConnecting: false,

      enqueueEvent: (event) => {
        set((state) => {
          const dedupKey = getEventDedupKey(event);
          let nextQueue: RealtimeEvent[];
          if (dedupKey) {
            // Remove existing event with same dedup key
            const filtered = state.queue.filter((e) => getEventDedupKey(e) !== dedupKey);
            nextQueue = filtered.length < state.queue.length ? filtered : [...state.queue];
          } else {
            nextQueue = [...state.queue];
          }
          nextQueue.push(event);

          // Prevent unbounded queue growth while tab is hidden. Dropping
          // events means the queue is no longer a faithful replay — flag it so
          // visibility-return forces a full resync instead of replaying a
          // partial history (E5).
          let overflowed = state.queueOverflowed;
          if (nextQueue.length > MAX_QUEUE_SIZE) {
            nextQueue.splice(0, nextQueue.length - MAX_QUEUE_SIZE);
            overflowed = true;
          }

          return { queue: nextQueue, queueOverflowed: overflowed };
        });

        const { documentVisible, processing } = get();
        if (documentVisible && !processing) {
          void get().processQueue();
        }
      },

      processQueue: async () => {
        if (get().processing) {
          return;
        }
        set({ processing: true });
        const context: RealtimeEventContext = {
          scheduleMailboxReload: (accountId: string, folderId: string) => {
            get().scheduleMailboxReload(accountId, folderId);
          },
        };
        try {
          while (true) {
            if (!get().documentVisible) {
              break;
            }
            const currentQueue = get().queue;
            if (!currentQueue.length) {
              break;
            }
            // Drain all events at once to avoid per-event state updates
            set({ queue: [] });
            for (let i = 0; i < currentQueue.length; i++) {
              try {
                await handleRealtimeEvent(currentQueue[i], context);
              } catch (error) {
                console.error('Failed to process realtime event', error);
              }
              if (!get().documentVisible) {
                // Tab hidden mid-drain: re-enqueue the unprocessed remainder we
                // already pulled out of the queue so it isn't silently lost
                // (audit §2.10 #6). Prepend ahead of anything enqueued meanwhile.
                const remaining = currentQueue.slice(i + 1);
                if (remaining.length) {
                  set((state) => ({ queue: [...remaining, ...state.queue] }));
                }
                break;
              }
            }
          }
        } finally {
          set({ processing: false });
        }
      },

      setDocumentVisibility: (visible) => {
        const wasVisible = get().documentVisible;
        const hiddenAt = get().hiddenAt;

        if (!visible && wasVisible) {
          // Track when we became hidden
          set({ documentVisible: false, hiddenAt: Date.now() });
          return;
        }

        if (visible && !wasVisible) {
          const hiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0;
          const overflowed = get().queueOverflowed;
          set({ documentVisible: true, hiddenAt: null, queueOverflowed: false });

          if (hiddenDuration > HIDDEN_THRESHOLD_MS || overflowed) {
            // Extended absence (5+ min) - clear queue and force reload
            if (get().queue.length > 0) {
              set({ queue: [] });
            }

            const accountId = useAccountStore.getState().selectedAccountId;
            const folderId = useFolderStore.getState().selectedFolderId();
            if (accountId && folderId) {
              get().scheduleMailboxReload(accountId, folderId);
            }
            // The dropped queue may have contained mailbox.counts events —
            // the mailbox reload alone never reconciles folder badges (N24).
            if (accountId) {
              void useFolderStore
                .getState()
                .loadFolders(accountId, { background: true })
                .catch(() => undefined);
            }
          } else {
            // Brief absence - process queued events normally
            if (!get().processing && get().queue.length > 0) {
              void get().processQueue();
            }
          }
        }
      },

      scheduleMailboxReload: (accountId, folderId) => {
        if (!accountId || !folderId) {
          return;
        }
        set({ reloadRequest: { accountId, folderId } });
        if (typeof window === 'undefined') {
          get().flushScheduledReload();
          return;
        }

        const now = Date.now();
        const deadline = get().reloadDeadline ?? now + RELOAD_MAX_DELAY_MS;
        const existingTimer = get().reloadTimer;
        if (existingTimer !== null) {
          window.clearTimeout(existingTimer);
        }

        const remaining = Math.max(0, deadline - now);
        const delay = remaining <= 0 ? 0 : Math.min(RELOAD_DEBOUNCE_MS, remaining);
        const timer = window.setTimeout(() => {
          set({ reloadTimer: null, reloadDeadline: null });
          get().flushScheduledReload();
        }, delay);

        set({
          reloadTimer: timer,
          reloadDeadline: deadline,
        });
      },

      cancelScheduledReload: () => {
        if (typeof window === 'undefined') {
          return;
        }
        const { reloadTimer } = get();
        if (reloadTimer !== null) {
          window.clearTimeout(reloadTimer);
        }
        set({
          reloadTimer: null,
          reloadDeadline: null,
          reloadRequest: null,
        });
      },

      flushScheduledReload: () => {
        const request = get().reloadRequest;
        if (!request) {
          return;
        }
        set({ reloadRequest: null, reloadDeadline: null });
        mailEvents.emit('mail:mailbox:reload', {
          accountId: request.accountId,
          folderId: request.folderId,
          options: { reset: true },
        });
      },

      setRealtimeStatus: (status) => set({ realtimeStatus: status }),

      setShowRealtimeConnecting: (visible) => set({ showRealtimeConnecting: visible }),

      clearQueue: () => set({ queue: [] }),
    }),
    { name: 'RealtimeStore' }
  )
);
